/**
 * EN 1998-5:2004 Ek F (bilgi amaçlı) — Yüzeysel temellerin sismik taşıma gücü.
 *
 * Kaynak: EN 1998-5:2004, Annex F "Seismic bearing capacity of shallow foundations" (Paolucci & Pecker 1997;
 * Pecker 1997 türetimi). Homojen zemin YÜZEYİNDEKİ şerit temel için taşıma yüzeyi eşitsizliği:
 *
 *   (1 − e·F̄)^cT · (β·V̄)^cT            (1 − f·F̄)^c'M · (γ·M̄)^cM
 *   ───────────────────────────────  +  ─────────────────────────────  − 1 ≤ 0                       (F.1)
 *   N̄^a · [(1 − m·F̄^k)^k' − N̄]^b        N̄^c · [(1 − m·F̄^k)^k' − N̄]^d
 *
 *   N̄ = γRd·N_Ed/N_max,  V̄ = γRd·V_Ed/N_max,  M̄ = γRd·M_Ed/(B·N_max)                                (F.2)
 *
 * Kohezyonlu (drenajsız) zemin:  N_max = (π+2)·c̄·B/γ_M (F.3);  F̄ = ρ·a_g·S·B/c̄ (F.4);  0 < N̄ ≤ 1, |V̄| ≤ 1 (F.5)
 * Sürtünmeli (drenajlı) zemin:   N_max = ½·ρ·g·(1 ± a_v/g)·B²·N_γ (F.6);  F̄ = a_g·S/(g·tanφ'_d) (F.7);
 *                                0 < N̄ ≤ (1 − m·F̄^k)^k' (F.8)
 *
 * Bu modül TBDY hükmünü ETKİLEMEZ; sonuç yalnız bilgi ve rapor bölümü içindir (spec §4.2, v1.0 §12a kararı).
 * Tablo F.1 / F.2 değerleri ve F.1–F.8 metni doğrulandı (2026-09-19): Geostru LoadCap yardım sayfası (EN 1998-5 Ek F'nin
 * tablo görselleri) + Chatzigogos–Pecker–Salençon 2007 (kohezyonlu sütun, Tablo 1 birebir); bkz. docs/research/07-ek-f-dogrulama.md.
 */

export type Ec8SoilType = "cohesive" | "cohesionless";

/** Tablo F.2 kategorileri (γRd model kısmi katsayısı). */
export type Ec8GammaRdCategory = "dense-sand" | "loose-dry-sand" | "loose-saturated-sand" | "non-sensitive-clay" | "sensitive-clay";

/** Tablo F.1 — (F.1) ifadesindeki sayısal parametreler. */
export interface AnnexFParams { a: number; b: number; c: number; d: number; e: number; f: number; m: number; k: number; kPrime: number; cT: number; cM: number; cMPrime: number; beta: number; gamma: number }

// EN 1998-5:2004 Tablo F.1 (purely cohesive / purely cohesionless) — doğrulandı, iki sütun da birebir (docs/research/07-ek-f-dogrulama.md).
export const ANNEX_F_TABLE_F1: Record<Ec8SoilType, AnnexFParams> = {
  cohesive:     { a: 0.70, b: 1.29, c: 2.14, d: 1.81, e: 0.21, f: 0.44, m: 0.21, k: 1.22, kPrime: 1.00, cT: 2.00, cM: 2.00, cMPrime: 1.00, beta: 2.57, gamma: 1.85 },
  cohesionless: { a: 0.92, b: 1.25, c: 0.92, d: 1.25, e: 0.41, f: 0.32, m: 0.96, k: 1.00, kPrime: 0.39, cT: 1.14, cM: 1.01, cMPrime: 1.01, beta: 2.90, gamma: 2.80 },
};

// EN 1998-5:2004 Tablo F.2 (γRd) — doğrulandı: orta sıkı–sıkı kum 1,00 · gevşek kuru kum 1,15 · gevşek doygun kum 1,50 · hassas olmayan kil 1,00 · hassas kil 1,15.
export const ANNEX_F_TABLE_F2: Record<Ec8GammaRdCategory, number> = {
  "dense-sand": 1.00, "loose-dry-sand": 1.15, "loose-saturated-sand": 1.50, "non-sensitive-clay": 1.00, "sensitive-clay": 1.15,
};

/** EN 1998-5 §3.1(3): malzeme kısmi katsayıları — tanφ' için 1,25; c_u için 1,4 (öneri değerleri). */
export const EC8_GAMMA_M = { tanPhi: 1.25, cu: 1.4 } as const;

/** (F.8) / (F.5) üst sınırı: N̄ ≤ (1 − m·F̄^k)^k'. */
export function annexFNbarLimit(soil: Ec8SoilType, Fbar: number): number {
  const p = ANNEX_F_TABLE_F1[soil];
  const inner = 1 - p.m * Math.pow(Math.max(0, Fbar), p.k);
  return inner > 0 ? Math.pow(inner, p.kPrime) : 0;
}

/**
 * (F.1) sol tarafı: f = term_V + term_M − 1. f < 0 → taşıma yüzeyinin içinde (sağlıyor).
 * Kısıtlar (F.5)/(F.8) ihlal edildiğinde yüzey dışındadır → +Infinity döner.
 * V̄ ve M̄ mutlak değerle alınır (kesirli üsler için; standart |V̄| ≤ 1 kısıtını verir).
 */
export function annexFSurface(soil: Ec8SoilType, Nbar: number, Vbar: number, Mbar: number, Fbar: number): number {
  const p = ANNEX_F_TABLE_F1[soil];
  const lim = annexFNbarLimit(soil, Fbar);
  const v = Math.abs(Vbar), mm = Math.abs(Mbar);
  if (!(Nbar > 0) || Nbar >= lim) return Infinity;
  if (soil === "cohesive" && v > 1) return Infinity;
  const eF = 1 - p.e * Fbar, fF = 1 - p.f * Fbar;
  if (eF <= 0 || fF <= 0) return Infinity;
  const termV = (Math.pow(eF, p.cT) * Math.pow(p.beta * v, p.cT)) / (Math.pow(Nbar, p.a) * Math.pow(lim - Nbar, p.b));
  const termM = (Math.pow(fF, p.cMPrime) * Math.pow(p.gamma * mm, p.cM)) / (Math.pow(Nbar, p.c) * Math.pow(lim - Nbar, p.d));
  return termV + termM - 1;
}

export interface Ec8BearingInput {
  soil: Ec8SoilType;
  /** Temel genişliği B (m) — Ek F gerçek genişliği kullanır, dış merkezlilik M̄ ile girer */
  B: number;
  /** Tasarım etkileri temel tabanında: N_Ed, V_Ed (kN/m), M_Ed (kNm/m, taban merkezine göre) */
  N: number; V: number; M: number;
  /** Zemin birim hacim ağırlığı ρ·g (kN/m³; su altında batık) */
  gamma: number;
  /** a_g·S/g — tasarım yer ivmesi × zemin katsayısı (g cinsinden) */
  agS: number;
  /** Sürtünmeli zemin: karakteristik φ' (rad) ve düşey ivme çarpanı (1 ± a_v/g) */
  phi?: number; kvFactor?: number;
  /** Kohezyonlu zemin: drenajsız kayma dayanımı c_u (kN/m²) */
  cu?: number;
  gammaRd: number;
  /** Malzeme kısmi katsayıları (varsayılan EN 1998-5 §3.1(3)) */
  gammaM?: { tanPhi: number; cu: number };
}

export interface Ec8BearingResult {
  soil: Ec8SoilType;
  Nmax: number; Ngamma?: number; phiDdeg?: number; cuD?: number;
  Nbar: number; Vbar: number; Mbar: number; Fbar: number; NbarLimit: number;
  /** (F.1) sol tarafı; f < 0 sağlıyor */
  f: number; ok: boolean;
  gammaRd: number; agS: number;
  notes: string[];
}

/** N_γ = 2·(N_q − 1)·tanφ (EN 1997-1 Ek D; pürüzlü taban). Ek F metni "see E.4" der (bilinen hatalı atıf; EN 1998-5 Ek E'de N_γ yok) → EC7 Ek D formülü kullanıldı. */
export function ngammaEc7(phi: number): number {
  if (phi < 1e-9) return 0;
  const Nq = Math.exp(Math.PI * Math.tan(phi)) * Math.tan(Math.PI / 4 + phi / 2) ** 2;
  return 2 * (Nq - 1) * Math.tan(phi);
}

export function bearingEc8AnnexF(p: Ec8BearingInput): Ec8BearingResult {
  const gM = p.gammaM ?? EC8_GAMMA_M;
  const notes: string[] = [];
  let Nmax: number, Fbar: number, Ngamma: number | undefined, phiDdeg: number | undefined, cuD: number | undefined;
  if (p.soil === "cohesionless") {
    const phi = p.phi ?? 0;
    const tanPhiD = Math.tan(phi) / gM.tanPhi;
    const phiD = Math.atan(tanPhiD);
    phiDdeg = (phiD * 180) / Math.PI;
    Ngamma = ngammaEc7(phiD);
    const kvF = p.kvFactor ?? 1;
    Nmax = 0.5 * p.gamma * kvF * p.B * p.B * Ngamma;                     // (F.6), ρ·g = γ
    Fbar = tanPhiD > 1e-12 ? p.agS / tanPhiD : Infinity;                 // (F.7)
    notes.push(`N_max = ½·γ·(1 ± a_v/g)·B²·N_γ, φ'_d = atan(tanφ'/${gM.tanPhi}) (EN 1998-5 §3.1(3))`);
  } else {
    const cu = p.cu ?? 0;
    cuD = cu / gM.cu;
    Nmax = (Math.PI + 2) * cuD * p.B;                                    // (F.3)
    Fbar = cu > 1e-12 ? (p.gamma * p.agS * p.B) / cu : Infinity;         // (F.4): ρ·(a_g·S)·B/c̄ = γ·(a_gS/g)·B/c_u — c̄ karakteristik (γ_M yalnız (F.3)'te; metinle doğrulandı)
    notes.push(`N_max = (π+2)·c_u·B/${gM.cu} (EN 1998-5 §3.1(3))`);
  }
  if (!(Nmax > 0) || !Number.isFinite(Fbar)) {
    return { soil: p.soil, Nmax, Ngamma, phiDdeg, cuD, Nbar: Infinity, Vbar: Infinity, Mbar: Infinity, Fbar, NbarLimit: 0, f: Infinity, ok: false, gammaRd: p.gammaRd, agS: p.agS, notes: [...notes, "N_max tanımsız (zemin dayanım parametresi sıfır)"] };
  }
  const Nbar = (p.gammaRd * p.N) / Nmax;
  const Vbar = (p.gammaRd * Math.abs(p.V)) / Nmax;
  const Mbar = (p.gammaRd * Math.abs(p.M)) / (p.B * Nmax);
  const NbarLimit = annexFNbarLimit(p.soil, Fbar);
  const f = annexFSurface(p.soil, Nbar, Vbar, Mbar, Fbar);
  if (!Number.isFinite(f)) notes.push(p.soil === "cohesive" ? "Kısıt (F.5): 0 < N̄ ≤ 1, |V̄| ≤ 1 sağlanmıyor" : "Kısıt (F.8): 0 < N̄ ≤ (1 − m·F̄^k)^k' sağlanmıyor");
  return { soil: p.soil, Nmax, Ngamma, phiDdeg, cuD, Nbar, Vbar, Mbar, Fbar, NbarLimit, f, ok: f < 0, gammaRd: p.gammaRd, agS: p.agS, notes };
}
